import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log('Attempting login with credentials:', credentials);
      const response = await api.post('/users/login', credentials);
      console.log('Login response:', response);
      if (response?.data?.token) {
        localStorage.setItem('token', response.data.token);
        return {
          ...response.data.user,
          passwordResetRequired: response.data.user.passwordResetRequired
        };
      } else {
        return rejectWithValue('Login failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      return rejectWithValue(error.response?.data?.message ?? 'Login failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/users/refresh-token');
      if (response && response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        return response.data.user;
      } else {
        return rejectWithValue('Token refresh failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return rejectWithValue(error.response?.data?.message || 'Token refresh failed');
    }
  }
);

export const updateUserSettings = createAsyncThunk(
  'auth/updateUser',
  async (updates, { rejectWithValue }) => {
    try {
      const response = await api.put('/users/settings', updates);
      if (response && response.data && response.data.user) {
        return response.data.user;
      } else {
        return rejectWithValue('Update failed: Unexpected response format');
      }
    } catch (error) {
      console.error('User update error:', error);
      return rejectWithValue(error.response?.data?.message || 'Update failed');
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users/profile');
      if (response && response.data) {
        return response.data;
      } else {
        return rejectWithValue('Profile fetch failed: Unexpected response format');
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      return rejectWithValue(error.response?.data?.message || 'Profile fetch failed');
    }
  }
);

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch }) => {
    console.log('Initializing auth state');
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Found existing token, fetching user profile');
      try {
        return await dispatch(fetchUserProfile()).unwrap();
      } catch (error) {
        console.error('Failed to fetch profile during initialization:', error);
        localStorage.removeItem('token');
        return null;
      }
    }
    console.log('No token found during initialization');
    return null;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    status: 'idle',
    error: null,
    passwordResetRequired: false,
    isInitialized: false
  },
  reducers: {
    logout: (state) => {
      console.log('Logging out user');
      state.user = null;
      state.token = null;
      state.passwordResetRequired = false;
      state.isInitialized = true;
      localStorage.removeItem('token');
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeAuth.pending, (state) => {
        console.log('Auth initialization pending');
        state.status = 'loading';
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        console.log('Auth initialization complete:', action.payload);
        state.status = 'succeeded';
        state.user = action.payload;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        console.error('Auth initialization failed:', action.error);
        state.status = 'failed';
        state.error = action.error.message;
        state.isInitialized = true;
      })
      .addCase(loginUser.pending, (state) => {
        console.log('Login pending');
        state.status = 'loading';
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        console.log('Login succeeded:', action.payload);
        state.status = 'succeeded';
        state.user = action.payload;
        state.token = localStorage.getItem('token');
        state.passwordResetRequired = action.payload.passwordResetRequired || false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.user = action.payload;
        state.token = localStorage.getItem('token');
      })
      .addCase(updateUserSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.user = action.payload;
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
